import React from 'react';
import { Box, Typography, Paper, Chip, useTheme } from '@mui/material';
import {
    PlayArrow as StartIcon,
    Person as PersonIcon,
    CheckCircle as CheckIcon,
    Cancel as CancelIcon,
} from '@mui/icons-material';
import type { OdpovedTimeline } from '../api/JsdhNotifikaceService/generated/models/OdpovedTimeline';
import { formatujDobuReakce } from '../utils/statistikyUtils';

interface VerticalTimelineProps {
    timeline: OdpovedTimeline[];
    casVyhlaseni: string;
    nejrychlejsiIds?: number[];
}

interface TimelineItemProps {
    icon: React.ReactNode;
    iconBgColor: string;
    title: string;
    subtitle?: string;
    time: string;
    isLast?: boolean;
    highlight?: boolean;
    children?: React.ReactNode;
}

const TimelineItem: React.FC<TimelineItemProps> = ({
    icon,
    iconBgColor,
    title,
    subtitle,
    time,
    isLast = false,
    highlight = false,
    children,
}) => {
    const theme = useTheme();

    return (
        <Box sx={{ display: 'flex', position: 'relative' }}>
            {/* Vertikalni cara */}
            {!isLast && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: 19,
                        top: 40,
                        bottom: -20,
                        width: 2,
                        bgcolor: 'grey.300',
                    }}
                />
            )}

            {/* Ikona */}
            <Box
                sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: iconBgColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    flexShrink: 0,
                    zIndex: 1,
                    boxShadow: highlight ? `0 0 0 4px ${theme.palette.warning.light}` : 'none',
                }}
            >
                {icon}
            </Box>

            {/* Obsah */}
            <Paper
                elevation={highlight ? 4 : 1}
                sx={{
                    ml: 2,
                    mb: 3,
                    p: 2,
                    flex: 1,
                    border: highlight ? `2px solid ${theme.palette.warning.main}` : 'none',
                }}
            >
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                            {title}
                        </Typography>
                        {subtitle && (
                            <Typography variant="body2" color="text.secondary">
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {time}
                    </Typography>
                </Box>
                {children}
            </Paper>
        </Box>
    );
};

const VerticalTimeline: React.FC<VerticalTimelineProps> = ({
    timeline,
    casVyhlaseni,
    nejrychlejsiIds = [],
}) => {
    const theme = useTheme();

    const sortedTimeline = [...timeline].sort((a, b) => a.dobaReakceSekundy - b.dobaReakceSekundy);

    const formatTime = (isoString: string): string => {
        return new Date(isoString).toLocaleTimeString('cs-CZ', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
    };

    return (
        <Box sx={{ py: 2 }}>
            {/* Vyhlaseni poplachu */}
            <TimelineItem
                icon={<StartIcon />}
                iconBgColor={theme.palette.error.main}
                title="Vyhlášení poplachu"
                time={formatTime(casVyhlaseni)}
            />

            {/* Odpovedi */}
            {sortedTimeline.map((odpoved, index) => {
                const jeJedu = odpoved.odpoved === 'JEDU';
                const isNejrychlejsi = nejrychlejsiIds.includes(odpoved.id);
                const poradi = nejrychlejsiIds.indexOf(odpoved.id) + 1;

                return (
                    <TimelineItem
                        key={odpoved.id}
                        icon={jeJedu ? <CheckIcon /> : <CancelIcon />}
                        iconBgColor={jeJedu ? theme.palette.success.main : theme.palette.error.main}
                        title={odpoved.uzivatelJmeno}
                        subtitle={odpoved.uzivatelFunkce?.map(f => f.kod).join(', ')}
                        time={formatTime(odpoved.cas)}
                        isLast={index === sortedTimeline.length - 1}
                        highlight={isNejrychlejsi}
                    >
                        <Box display="flex" alignItems="center" gap={1} mt={1} flexWrap="wrap">
                            <Chip
                                label={jeJedu ? 'JEDU' : 'NEJEDU'}
                                color={jeJedu ? 'success' : 'error'}
                                size="small"
                            />
                            <Chip
                                label={`Reakce: ${formatujDobuReakce(odpoved.dobaReakceSekundy)}`}
                                variant="outlined"
                                size="small"
                            />
                            {isNejrychlejsi && (
                                <Chip
                                    label={poradi === 1 ? '1. nejrychlejší' : poradi === 2 ? '2. nejrychlejší' : '3. nejrychlejší'}
                                    sx={{
                                        bgcolor: poradi === 1 ? '#FFD700' : poradi === 2 ? '#C0C0C0' : '#CD7F32',
                                        color: poradi === 1 ? '#000' : '#fff',
                                    }}
                                    size="small"
                                />
                            )}
                        </Box>
                    </TimelineItem>
                );
            })}

            {sortedTimeline.length === 0 && (
                <Box sx={{ ml: 7, py: 2 }}>
                    <Typography color="text.secondary">
                        Zatím žádné odpovědi
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default VerticalTimeline;
